/**
 * Shared plan picker — ONE implementation, one copy source.
 *
 * There used to be two. `TopNav`'s upgrade dialog said "Choose your plan"
 * (`common:topNav.chooseYourPlan`) and `/system/billing` said "Choose a plan"
 * (`system:billing.planPickerTitle`); the yearly line was two different
 * sentences with two different implementations of the same arithmetic, one of
 * which divided by zero on the $0 plan and rendered "Save NaN%"; even
 * `perMonth` had drifted to "/ month" vs "/month". Four of the CTA strings
 * existed twice, in two namespaces. This exact pair of files had already
 * produced one production incident by drifting on plan SORT order
 * (svdy-rules §10.5), which is what makes a third divergence a matter of when.
 *
 * What is new here, beyond de-duplication:
 *
 *  - A real monthly / annual **toggle**. Both dialogs advertised
 *    "$790/year · save 17%" as static text while the backend hardcoded
 *    `interval="month"`, so annual billing was unbuyable — an unfulfillable
 *    claim on the page a buyer decides on (MARKETING_COPY §18.2). The toggle
 *    drives `billing_interval` end to end.
 *  - A confirm step that itemises what will actually be charged: base plan,
 *    extra workers beyond the allotment, total. This is the transparency the
 *    seat-selection discussion was really after. It is deliberately NOT an
 *    editable seat input: svdy prices as "included quota + overage", where the
 *    headcount is a fact we already know, so an editable number would be a
 *    third source of truth for "how many people may this org have" alongside
 *    `included_workers` and `max_workers`, and would raise a question with no
 *    good answer (buyer types 30, company has 45 — do the other 15 stop
 *    clocking in?). Products that ask for a seat count are pure per-seat with
 *    no included quota (Slack, Notion, Linear, Jira); products shaped like this
 *    one (QuickBooks Time, Gusto, Homebase, Deputy) show the count and bill it.
 */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, Chip, CircularProgress, Divider, Paper, Stack, Tab, Tabs,
  Typography,
} from '@mui/material';

import {
  PLAN_FEATURE_ROW_GAP_PX,
  PRODUCT_CHIP_META,
  PlanFeatureRow,
  getPlanBullets,
  getPlanDescription,
  getPlanDisplayName,
  getPlanHighlightLabel,
  getProductLine,
  planOverageRate,
  sortPlansByTier,
} from './planMeta';
import type { Plan } from '../../api/subscription';
import { activeLocale } from '../../utils/datetime';

export type BillingInterval = 'month' | 'year';
export type ProductLine = 'qutime' | 'pureoa' | 'bundle';

/** Parse a price that arrives as a string, a number, or null (Enterprise). */
function parsePrice(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : parseFloat(String(value));
  return Number.isFinite(n) ? n : null;
}

/**
 * Annual saving as a whole percentage, or null when it cannot be computed.
 *
 * The guard is the whole point: `price_monthly` arrives as the STRING `"0.00"`
 * for free plans, and `"0.00"` is truthy in JS, so the previous
 * `p.price_monthly ? Math.round(100 - (yearly / (monthly * 12)) * 100) : 0`
 * evaluated `100 - (0 / 0) * 100` and rendered **"Save NaN%"** on the Free card.
 */
export function annualSavingPct(
  monthly: number | null, yearly: number | null,
): number | null {
  if (monthly === null || yearly === null) return null;
  if (monthly <= 0 || yearly <= 0) return null;
  const pct = Math.round(100 - (yearly / (monthly * 12)) * 100);
  return pct > 0 ? pct : null;
}

/** Price for the selected cadence. Null means "no price" (contact sales). */
export function priceFor(plan: Plan, interval: BillingInterval): number | null {
  return interval === 'year'
    ? parsePrice(plan.price_yearly)
    : parsePrice(plan.price_monthly);
}

/**
 * Per-extra-worker rate for the selected cadence.
 *
 * `Plan.prices` carries both `per_extra_worker_monthly` and
 * `per_extra_worker_yearly`; showing the monthly rate next to an annual total
 * would understate the annual cost of an extra worker by 12x.
 */
export function overageRateFor(
  plan: Plan, interval: BillingInterval, currency = 'USD',
): number | null {
  const prices = (plan as unknown as {
    prices?: Record<string, Record<string, unknown>> | null;
  }).prices;
  const entry = prices?.[currency.toUpperCase()] ?? prices?.USD;
  if (entry) {
    const raw = entry[
      interval === 'year' ? 'per_extra_worker_yearly' : 'per_extra_worker_monthly'
    ];
    const n = parsePrice(raw);
    if (n !== null && n > 0) return n;
  }
  // Monthly falls back to the shared helper so a plan whose prices JSON only
  // carries the legacy shape still shows a rate.
  return interval === 'month' ? planOverageRate(plan, currency) : null;
}

export interface SeatBreakdown {
  included: number | null;
  /** Null when the caller could not tell us the headcount — the panel then
   *  omits the team line rather than printing "0 active workers", which would
   *  be a confident lie about the customer's own company. */
  activeWorkers: number | null;
  extraWorkers: number;
  basePrice: number;
  overageRate: number | null;
  overageAmount: number;
  total: number;
}

/** What the customer will actually be charged, itemised. */
export function computeBreakdown(
  plan: Plan, interval: BillingInterval, activeWorkers: number | null,
  currency = 'USD',
): SeatBreakdown | null {
  const basePrice = priceFor(plan, interval);
  if (basePrice === null) return null;
  const included = plan.included_workers ?? null;
  const rate = overageRateFor(plan, interval, currency);
  const extra =
    activeWorkers !== null && rate !== null && included !== null
      && activeWorkers > included
      ? activeWorkers - included
      : 0;
  const overageAmount = rate !== null ? extra * rate : 0;
  return {
    included,
    activeWorkers,
    extraWorkers: extra,
    basePrice,
    overageRate: rate,
    overageAmount,
    total: basePrice + overageAmount,
  };
}

function formatMoney(amount: number): string {
  // `activeLocale()` is mandatory, not stylistic: a bare `toLocaleString()`
  // formats against the BROWSER's language, so a zh-CN user could see a price
  // grouped by their OS locale while the rest of the page is in the app's.
  // `localeFormatting.test.ts` fails the build on a bare call — it caught this
  // very line.
  const locale = activeLocale();
  return amount % 1 === 0
    ? `$${amount.toLocaleString(locale, { maximumFractionDigits: 0 })}`
    : `$${amount.toLocaleString(locale, {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
      })}`;
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

const PlanCard: React.FC<{
  plan: Plan;
  interval: BillingInterval;
  currency: string;
  isCurrent: boolean;
  isRecommended: boolean;
  onSelect: () => void;
  onContactSales: () => void;
}> = ({ plan, interval, currency, isCurrent, isRecommended, onSelect, onContactSales }) => {
  const { t } = useTranslation();
  const price = priceFor(plan, interval);
  const monthly = parsePrice(plan.price_monthly);
  const yearly = parsePrice(plan.price_yearly);
  const savingPct = annualSavingPct(monthly, yearly);
  const displayName = getPlanDisplayName(plan, t);
  const description = getPlanDescription(plan, t);
  const highlightLabel = getPlanHighlightLabel(plan, t);
  const featureLabels = getPlanBullets(plan, t);
  const rate = overageRateFor(plan, interval, currency);
  const included = plan.included_workers;
  const isContactSales = plan.tier === 'enterprise' || price === null;

  const workersLabel =
    included === null || included === undefined
      ? t('common:planCard.unlimitedWorkers')
      : rate != null
        ? t('common:planCard.includesWorkers', { count: included })
        : t('common:planCard.upToWorkers', { count: included });

  const products = (plan.licensed_products ?? []).filter(
    (p): p is string => typeof p === 'string',
  );

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        borderRadius: 2,
        border: '1px solid',
        borderColor: isCurrent
          ? 'primary.main'
          : isRecommended
            ? 'primary.light'
            : 'divider',
        position: 'relative',
        transition: 'border-color 120ms',
        '&:hover': { borderColor: isCurrent ? 'primary.main' : 'text.disabled' },
      }}
    >
      {(isCurrent || highlightLabel) && (
        <Chip
          label={isCurrent ? t('common:planPicker.currentPlan') : highlightLabel}
          size="small"
          color={isCurrent ? 'primary' : 'default'}
          sx={{
            position: 'absolute', top: -10, right: 12, height: 20,
            fontSize: '0.65rem', fontWeight: 700, borderRadius: 1,
          }}
        />
      )}

      <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
        {displayName}
      </Typography>

      {products.length > 0 && (
        <Stack direction="row" gap={0.5} sx={{ mt: 0.75, flexWrap: 'wrap' }}>
          {products.map((prod) => {
            const meta = PRODUCT_CHIP_META[prod] ?? {
              label: prod, color: '#616161', bg: 'rgba(97,97,97,0.1)',
            };
            return (
              <Chip
                key={prod}
                label={meta.label}
                size="small"
                sx={{
                  height: 18, fontSize: '0.6rem', fontWeight: 700,
                  letterSpacing: '0.02em', bgcolor: meta.bg, color: meta.color,
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            );
          })}
        </Stack>
      )}

      <Box sx={{ my: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1 }}>
            {price === null
              ? t('common:planPicker.custom')
              : formatMoney(price)}
          </Typography>
          {price !== null && price > 0 && (
            <Typography variant="caption" color="text.secondary">
              {interval === 'year'
                ? t('common:planPicker.perYear')
                : t('common:planPicker.perMonth')}
            </Typography>
          )}
        </Box>
        {price === 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {t('common:planPicker.foreverFree')}
          </Typography>
        )}
        {/* Only ever rendered when the percentage is real — see annualSavingPct. */}
        {interval === 'year' && savingPct !== null && (
          <Typography variant="caption" sx={{ display: 'block', color: 'success.main', fontWeight: 600 }}>
            {t('common:planPicker.savingVsMonthly', { percent: savingPct })}
          </Typography>
        )}
        {interval === 'month' && savingPct !== null && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {t('common:planPicker.orAnnual', { price: formatMoney(yearly as number), percent: savingPct })}
          </Typography>
        )}
      </Box>

      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: '0.78rem' }}>
          {description}
        </Typography>
      )}

      {/* `spacing={0.5}` (4px) was tighter than the 1.5-line leading INSIDE a wrapped label, so a
        * three-line item and its neighbour ran together and the column had no rhythm — reported as
        * 每个功能点的分行不一致，但是展示的没有分割. The gap BETWEEN items must exceed the gap between
        * lines within one item, or the eye cannot tell where an item ends.
        *
        * 0.72rem × 1.5 ≈ 17px line box; 1 unit = 8px sits clearly above that. Matches the
        * marketing card's `spacing={1.15}` scaled to this card's smaller type. */}
      <Stack spacing={`${PLAN_FEATURE_ROW_GAP_PX}px`} sx={{ mb: 1.5 }}>
        {/* The overage rate is the REST OF THIS SENTENCE, so it is this row's `note` rather than a
          * sibling with a hand-tuned indent and a negative margin to cancel the Stack gap. Both of
          * those were guesses about a box model, MUI's Stack spacing selector out-specifies an
          * element's own `mt`, and the owner reported it misaligned twice. Structure, not numbers. */}
        <PlanFeatureRow
          label={workersLabel}
          note={rate != null
            ? String(interval === 'year'
              ? t('common:planPicker.perExtraWorkerYear', { rate })
              : t('common:planCard.perExtraWorker', { rate }))
            : undefined}
        />
        {featureLabels.map((label) => (
          <PlanFeatureRow key={label} label={label} />
        ))}
        {(plan.trial_days ?? 0) > 0 && !isCurrent && (
          <Typography variant="caption" color="primary" sx={{ fontWeight: 600, mt: 0.5, fontSize: '0.7rem' }}>
            {t('common:planPicker.trialDays', { days: plan.trial_days })}
          </Typography>
        )}
      </Stack>

      <Box sx={{ mt: 'auto' }}>
        <Button
          variant={isCurrent || !isRecommended ? 'outlined' : 'contained'}
          fullWidth
          size="small"
          disabled={isCurrent}
          onClick={isCurrent ? undefined : isContactSales ? onContactSales : onSelect}
          sx={{
            borderRadius: 1, textTransform: 'none', fontSize: '0.8rem',
            fontWeight: 600, whiteSpace: 'nowrap', minHeight: 36,
          }}
        >
          {isCurrent
            ? t('common:planPicker.currentPlan')
            : isContactSales
              ? t('common:planPicker.contactSales')
              : (plan.trial_days ?? 0) > 0
                ? t('common:planPicker.startFreeTrial')
                : t('common:planPicker.getStarted')}
        </Button>
      </Box>
    </Paper>
  );
};

// ---------------------------------------------------------------------------
// Confirm step — what will actually be charged
// ---------------------------------------------------------------------------

const ConfirmPanel: React.FC<{
  plan: Plan;
  interval: BillingInterval;
  breakdown: SeatBreakdown;
  busy: boolean;
  onBack: () => void;
  onConfirm: () => void;
}> = ({ plan, interval, breakdown, busy, onBack, onConfirm }) => {
  const { t } = useTranslation();
  const perPeriod = interval === 'year'
    ? t('common:planPicker.perYear')
    : t('common:planPicker.perMonth');
  const row = (label: React.ReactNode, value: string, bold = false) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.5 }}>
      <Typography variant="body2" sx={{ fontWeight: bold ? 700 : 400 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: bold ? 700 : 400, whiteSpace: 'nowrap' }}>
        {value}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 480, mx: 'auto' }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
        {getPlanDisplayName(plan, t)}
      </Typography>
      {(plan.trial_days ?? 0) > 0 && (
        <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>
          {t('common:planPicker.trialDays', { days: plan.trial_days })}
        </Typography>
      )}

      <Divider sx={{ my: 1.5 }} />

      {row(
        breakdown.included == null
          ? t('common:planPicker.breakdownPlanUnlimited')
          : t('common:planPicker.breakdownPlan', { count: breakdown.included }),
        `${formatMoney(breakdown.basePrice)} ${perPeriod}`,
      )}

      {breakdown.extraWorkers > 0 && breakdown.overageRate !== null
        ? row(
            t('common:planPicker.breakdownExtra', {
              count: breakdown.extraWorkers,
              rate: breakdown.overageRate,
            }),
            `${formatMoney(breakdown.overageAmount)} ${perPeriod}`,
          )
        : breakdown.activeWorkers !== null
          ? row(
              t('common:planPicker.breakdownTeam', {
                count: breakdown.activeWorkers,
              }),
              t('common:planPicker.breakdownWithinPlan'),
            )
          : null}

      <Divider sx={{ my: 1.5 }} />

      {row(
        (plan.trial_days ?? 0) > 0
          ? t('common:planPicker.breakdownTotalAfterTrial')
          : t('common:planPicker.breakdownTotal'),
        `${formatMoney(breakdown.total)} ${perPeriod}`,
        true,
      )}
      {interval === 'year' && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right' }}>
          {t('common:planPicker.breakdownMonthlyEquivalent', {
            amount: formatMoney(Math.round((breakdown.total / 12) * 100) / 100),
          })}
        </Typography>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        {(plan.trial_days ?? 0) > 0
          ? t('common:planPicker.breakdownNoChargeToday')
          : t('common:planPicker.breakdownSeatNote')}
      </Typography>

      <Stack direction="row" spacing={1} sx={{ mt: 2.5 }}>
        <Button
          onClick={onBack}
          disabled={busy}
          sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}
        >
          {t('common:planPicker.back')}
        </Button>
        <Button
          variant="contained"
          fullWidth
          onClick={onConfirm}
          disabled={busy}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}
          sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem', fontWeight: 600 }}
        >
          {(plan.trial_days ?? 0) > 0
            ? t('common:planPicker.confirmStartTrial')
            : t('common:planPicker.confirmSubscribe')}
        </Button>
      </Stack>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// PlanPicker
// ---------------------------------------------------------------------------

export interface PlanPickerProps {
  plans: Plan[];
  /** Highlights the tenant's current plan and disables its CTA. */
  currentPlanId?: string | null;
  /** Active worker count, for the itemised breakdown. Omit to hide that line. */
  activeWorkers?: number | null;
  currency?: string;
  /** Set while a checkout / change-plan request is in flight. */
  busy?: boolean;
  /** Which product line to open on. Defaults to the current plan's line. */
  defaultProductLine?: ProductLine;
  onSelect: (planId: string, interval: BillingInterval) => void;
  onContactSales?: (plan: Plan) => void;
}

export const PlanPicker: React.FC<PlanPickerProps> = ({
  plans,
  currentPlanId,
  activeWorkers,
  currency = 'USD',
  busy = false,
  defaultProductLine,
  onSelect,
  onContactSales,
}) => {
  const { t } = useTranslation();
  const currentPlan = plans.find((p) => p.id === currentPlanId);
  const [line, setLine] = useState<ProductLine>(
    defaultProductLine ?? (currentPlan ? getProductLine(currentPlan) : 'bundle'),
  );
  const [interval, setInterval] = useState<BillingInterval>('month');
  const [pendingPlanId, setPendingPlanId] = useState<string | null>(null);

  const visible = useMemo(
    () => sortPlansByTier(plans.filter((p) => getProductLine(p) === line)),
    [plans, line],
  );

  /** Any plan in the catalogue that can actually be bought annually. */
  const annualAvailable = useMemo(
    () => plans.some((p) => {
      const y = parsePrice(p.price_yearly);
      return y !== null && y > 0;
    }),
    [plans],
  );

  const pendingPlan = plans.find((p) => p.id === pendingPlanId) ?? null;
  const breakdown = pendingPlan
    ? computeBreakdown(pendingPlan, interval, activeWorkers ?? null, currency)
    : null;

  const handleContactSales = (plan: Plan) => {
    if (onContactSales) {
      onContactSales(plan);
      return;
    }
    window.location.href = `mailto:sales@svdy.com?subject=${encodeURIComponent(
      t('common:planPicker.enterpriseInquirySubject', {
        plan: getPlanDisplayName(plan, t),
      }),
    )}`;
  };

  if (pendingPlan && breakdown) {
    return (
      <ConfirmPanel
        plan={pendingPlan}
        interval={interval}
        breakdown={breakdown}
        busy={busy}
        onBack={() => setPendingPlanId(null)}
        onConfirm={() => onSelect(pendingPlan.id, interval)}
      />
    );
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          gap: 1.5, mb: 2.5, flexWrap: 'wrap',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            borderRadius: 999, border: '1px solid rgba(0,0,0,0.08)', p: 0.5,
            bgcolor: '#fafafa',
          }}
        >
          <Tabs
            value={line}
            onChange={(_, v) => setLine(v as ProductLine)}
            TabIndicatorProps={{ style: { display: 'none' } }}
            sx={{
              minHeight: 0,
              '& .MuiTab-root': {
                minHeight: 0, py: 0.75, px: 2.5, borderRadius: 999,
                textTransform: 'none', fontSize: '0.8125rem', fontWeight: 600,
                color: 'text.secondary',
                '&.Mui-selected': { bgcolor: 'primary.main', color: '#fff' },
              },
            }}
          >
            <Tab value="qutime" label="QuTime" />
            <Tab value="pureoa" label="PureOA" />
            <Tab value="bundle" label="QuTime + PureOA" />
          </Tabs>
        </Paper>

        {/* Monthly / annual is a real control, not a sentence. Hidden entirely
            when no plan has an annual price — a toggle that changes nothing is
            worse than no toggle. */}
        {annualAvailable && (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 999, border: '1px solid rgba(0,0,0,0.08)', p: 0.5,
              bgcolor: '#fafafa',
            }}
          >
            <Tabs
              value={interval}
              onChange={(_, v) => setInterval(v as BillingInterval)}
              TabIndicatorProps={{ style: { display: 'none' } }}
              sx={{
                minHeight: 0,
                '& .MuiTab-root': {
                  minHeight: 0, py: 0.75, px: 2, borderRadius: 999,
                  textTransform: 'none', fontSize: '0.8125rem', fontWeight: 600,
                  color: 'text.secondary',
                  '&.Mui-selected': { bgcolor: 'primary.main', color: '#fff' },
                },
              }}
            >
              <Tab value="month" label={t('common:planPicker.billMonthly')} />
              <Tab value="year" label={t('common:planPicker.billAnnually')} />
            </Tabs>
          </Paper>
        )}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)',
          },
          gap: 2,
        }}
      >
        {visible.map((plan) => {
          const isCurrent = currentPlanId === plan.id;
          return (
            <PlanCard
              key={plan.id}
              plan={plan}
              interval={interval}
              currency={currency}
              isCurrent={isCurrent}
              isRecommended={plan.tier === 'professional' && !isCurrent}
              onSelect={() => setPendingPlanId(plan.id)}
              onContactSales={() => handleContactSales(plan)}
            />
          );
        })}
      </Box>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', textAlign: 'center', mt: 2 }}
      >
        {t('common:planPicker.allPaidPlans')}
      </Typography>
    </>
  );
};

export default PlanPicker;
